import { Pencil, Ban } from "lucide-react";
import { money, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";

const Head = ({ cols }) => (
  <TableHeader>
    <TableRow className="hover:bg-transparent">
      {cols.map((c) => <TableHead key={c} className="eyebrow h-11 whitespace-nowrap">{c}</TableHead>)}
    </TableRow>
  </TableHeader>
);

const MobileRow = ({ left, sub, right, badge, testid }) => (
  <li className="flex items-center justify-between gap-3 px-4 py-3" data-testid={testid}>
    <div className="min-w-0">
      <div className="text-sm font-medium">{left}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
    <div className="flex shrink-0 flex-col items-end gap-1">
      <span className="num text-sm font-semibold">{right}</span>
      {badge}
    </div>
  </li>
);

export const ScheduleTable = ({ rows }) => {
  const tot = rows.reduce((a, r) => ({ p: a.p + r.payment_cents, i: a.i + r.interest_cents, pr: a.pr + r.principal_cents }), { p: 0, i: 0, pr: 0 });
  return (
    <>
      <div className="hidden overflow-x-auto sm:block">
        <Table data-testid="schedule-table">
          <Head cols={["#", "Due date", "Payment", "Interest", "Principal", "Remaining balance", "Paid", "Status"]} />
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.number} data-testid={`schedule-row-${r.number}`}>
                <TableCell className="num text-muted-foreground">{r.number}</TableCell>
                <TableCell className="whitespace-nowrap">{fmtDate(r.due_date)}</TableCell>
                <TableCell className="num font-medium" data-testid={`schedule-payment-${r.number}`}>{money(r.payment_cents)}</TableCell>
                <TableCell className="num" data-testid={`schedule-interest-${r.number}`}>{money(r.interest_cents)}</TableCell>
                <TableCell className="num">{money(r.principal_cents)}</TableCell>
                <TableCell className="num" data-testid={`schedule-balance-${r.number}`}>{money(r.balance_cents)}</TableCell>
                <TableCell className="num text-muted-foreground">{money(r.paid_cents)}</TableCell>
                <TableCell><StatusBadge status={r.status} testid={`schedule-status-${r.number}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2} className="eyebrow">Totals</TableCell>
              <TableCell className="num font-semibold">{money(tot.p)}</TableCell>
              <TableCell className="num font-semibold" data-testid="schedule-total-interest">{money(tot.i)}</TableCell>
              <TableCell className="num font-semibold">{money(tot.pr)}</TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <ul className="divide-y sm:hidden" data-testid="schedule-list-mobile">
        {rows.map((r) => (
          <MobileRow key={r.number} testid={`schedule-mobile-${r.number}`} left={`#${r.number} · ${fmtDate(r.due_date)}`}
            sub={`Int ${money(r.interest_cents)} · Prin ${money(r.principal_cents)} · Bal ${money(r.balance_cents)}`}
            right={money(r.payment_cents)} badge={<StatusBadge status={r.status} />} />
        ))}
      </ul>
    </>
  );
};

export const PaymentsTable = ({ payments, readOnly, onEdit, onVoid }) => {
  if (!payments.length) return <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="payments-empty">No payments recorded yet.</div>;
  return (
    <>
      <div className="hidden overflow-x-auto sm:block">
        <Table data-testid="payments-table">
          <Head cols={["Date", "Amount", "Fees", "Interest", "Principal", "Excess", "Method", "Note", "Status", ...(readOnly ? [] : [""])]} />
          <TableBody>
            {payments.map((p) => {
              const a = p.allocation || {};
              const v = p.status === "voided";
              return (
                <TableRow key={p.id} className={v ? "text-muted-foreground line-through decoration-1" : ""} data-testid={`payment-row-${p.id}`}>
                  <TableCell className="whitespace-nowrap">{fmtDate(p.date)}</TableCell>
                  <TableCell className="num font-semibold" data-testid={`payment-amount-${p.id}`}>{money(p.amount_cents)}</TableCell>
                  <TableCell className="num" data-testid={`payment-fees-${p.id}`}>{v ? "—" : money(a.fees_cents)}</TableCell>
                  <TableCell className="num" data-testid={`payment-interest-${p.id}`}>{v ? "—" : money(a.interest_cents)}</TableCell>
                  <TableCell className="num" data-testid={`payment-principal-${p.id}`}>{v ? "—" : money(a.principal_cents)}</TableCell>
                  <TableCell className="num">{v || !a.excess_cents ? "—" : money(a.excess_cents)}</TableCell>
                  <TableCell className="whitespace-nowrap" data-testid={`payment-method-${p.id}`}>
                    {p.method}
                    {p.stripe_payment_id && <span className="mono block text-[11px] text-muted-foreground" data-testid={`payment-stripe-id-${p.id}`}>{p.stripe_payment_id}</span>}
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm" title={p.note}>{p.note || "—"}</TableCell>
                  <TableCell className="no-underline"><StatusBadge status={p.status} testid={`payment-status-${p.id}`} /></TableCell>
                  {!readOnly && (
                    <TableCell className="whitespace-nowrap text-right">
                      {!v && (
                        <div className="flex justify-end gap-1">
                          {!p.stripe_payment_id && <Button variant="ghost" size="sm" onClick={() => onEdit(p)} data-testid={`edit-payment-${p.id}`}><Pencil /> Edit</Button>}
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => onVoid(p)} data-testid={`void-payment-${p.id}`}><Ban /> Void</Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <ul className="divide-y sm:hidden" data-testid="payments-list-mobile">
        {payments.map((p) => {
          const a = p.allocation || {};
          return (
            <MobileRow key={p.id} testid={`payment-mobile-${p.id}`} left={`${fmtDate(p.date)} · ${p.method}`}
              sub={`Fees ${money(a.fees_cents)} · Int ${money(a.interest_cents)} · Prin ${money(a.principal_cents)}${p.stripe_payment_id ? ` · ${p.stripe_payment_id}` : ""}`}
              right={money(p.amount_cents)} badge={<StatusBadge status={p.status} />} />
          );
        })}
      </ul>
    </>
  );
};

const TYPE = { disbursement: "Disbursement", interest: "Interest", late_fee: "Late fee", payment: "Payment" };

export const LedgerTable = ({ entries }) => (
  <div className="overflow-x-auto">
    <Table data-testid="ledger-table">
      <Head cols={["Date", "Type", "Description", "Amount", "Principal bal.", "Interest bal.", "Fee bal.", "Total balance"]} />
      <TableBody>
        {entries.map((e, i) => (
          <TableRow key={i} data-testid={`ledger-row-${i}`}>
            <TableCell className="whitespace-nowrap">{fmtDate(e.date)}</TableCell>
            <TableCell className="whitespace-nowrap text-sm font-medium" data-testid={`ledger-type-${i}`}>{TYPE[e.type]}</TableCell>
            <TableCell className="min-w-[200px] text-sm text-muted-foreground">
              {e.description}
              {e.type === "payment" && <span className="block text-xs">fees {money(e.fees_cents)} · interest {money(e.interest_cents)} · principal {money(e.principal_cents)}{e.excess_cents ? ` · excess ${money(e.excess_cents)}` : ""}</span>}
            </TableCell>
            <TableCell className={`num font-medium ${e.type === "payment" ? "text-emerald-700" : ""}`}>{e.type === "payment" ? "−" : "+"}{money(e.amount_cents)}</TableCell>
            <TableCell className="num">{money(e.principal_balance_cents)}</TableCell>
            <TableCell className="num">{money(e.interest_balance_cents)}</TableCell>
            <TableCell className="num">{money(e.fee_balance_cents)}</TableCell>
            <TableCell className="num font-semibold">{money(e.total_balance_cents)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const FeesTable = ({ fees, readOnly, onWaive }) => {
  if (!fees.length) return <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="fees-empty">No late fees assessed.</div>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid="fees-table">
        <Head cols={["Installment", "Due date", "Assessed (effective)", "Amount", "Paid", "Status", ...(readOnly ? [] : ["Assessed by", ""])]} />
        <TableBody>
          {fees.map((f) => (
            <TableRow key={f.id} data-testid={`fee-row-${f.installment}`}>
              <TableCell className="num">#{f.installment}</TableCell>
              <TableCell>{fmtDate(f.due_date)}</TableCell>
              <TableCell>{fmtDate(f.effective_date)}</TableCell>
              <TableCell className="num font-semibold" data-testid={`fee-amount-${f.installment}`}>{money(f.amount_cents)}</TableCell>
              <TableCell className="num">{money(f.paid_cents)}</TableCell>
              <TableCell>
                <StatusBadge status={f.status} testid={`fee-status-${f.installment}`} />
                {f.status === "waived" && <div className="mt-1 max-w-[220px] text-xs text-muted-foreground" data-testid={`fee-waive-reason-${f.installment}`}>{fmtDate(f.waived_date)} · {f.waived_reason}</div>}
              </TableCell>
              {!readOnly && <TableCell className="text-sm text-muted-foreground">{f.assessed_by}</TableCell>}
              {!readOnly && (
                <TableCell className="text-right">
                  {f.status === "outstanding" && (
                    <Button variant="ghost" size="sm" onClick={() => onWaive(f)} data-testid={`waive-fee-${f.installment}`}><Ban /> Waive</Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
